import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/lib/cn.ts';
import { focusCancel } from '@/lib/dialog-focus.ts';

/**
 * A confirmation for an action that cannot be undone.
 *
 * Radix rather than a hand-rolled overlay, because the parts that make a dialog usable are the
 * parts that are tedious to remember: focus moves into it and is trapped, Escape closes it, the
 * page behind is inert to a screen reader, and focus returns to the trigger afterwards. Getting
 * any of those wrong is invisible to whoever built it and immediately obvious to a keyboard user.
 *
 * The destructive action is **not** the default focus. A dialog that puts the cursor on "Delete"
 * turns a reflexive Enter into the thing it exists to prevent.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = false,
  busy = false,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm(): void;
  destructive?: boolean;
  busy?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]" />
        <Dialog.Content
          className={cn(
            'bg-card fixed top-1/2 left-1/2 z-50 grid w-[calc(100vw-2rem)] max-w-md -translate-x-1/2',
            '-translate-y-1/2 gap-4 rounded-lg border p-5 shadow-lg',
          )}
          // Focus lands on Cancel, not the destructive button.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            focusCancel(event.currentTarget as HTMLElement);
          }}
        >
          <div className="grid gap-1.5">
            <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            <Dialog.Description className="text-muted-foreground text-sm">{description}</Dialog.Description>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <Button variant="outline" data-cancel>Cancel</Button>
            </Dialog.Close>
            <Button variant={destructive ? 'destructive' : 'default'} disabled={busy} onClick={onConfirm}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
